import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  GetAccount200ResponsePositionsInner,
  GetRfqs200ResponseBlocksInner,
  GetRfqsBlockIdQuotes200ResponseAsksInner,
  SideResponse,
} from "../../codegen-api";
import { getUsdcDecimals } from "../../config/chains";
import { toTokenUnits } from "../../utils/decimalString";
import { ToastEnum, ToastStatusEnum } from "../../utils/toast";
import { useRFQ } from "../../hooks/api/rfq/useRFQ";
import { ITakerQuotePayload, useQuotes } from "../../hooks/api/rfq/useQuotes";
import { useToast } from "../../hooks/toast";
import { ClosePositionPageEnum } from "./ClosePositionModal.types";

type TranslateFn = (...args: any[]) => any;

interface IUseClosePositionRFQParams {
  position: GetAccount200ResponsePositionsInner | undefined;
  getRFQQuotesByBlockId: ReturnType<typeof useRFQ>["getRFQQuotesByBlockId"];
  deleteRFQsByInstrumentId: ReturnType<
    typeof useRFQ
  >["deleteRFQsByInstrumentId"];
  rfqData: ReturnType<typeof useRFQ>["data"];
  createRFQ: ReturnType<typeof useRFQ>["createRFQ"];
  acceptQuote: ReturnType<typeof useQuotes>["acceptQuote"];
  addErrorToast: ReturnType<typeof useToast>["addErrorToast"];
  addToast: ReturnType<typeof useToast>["addToast"];
  apiError: TranslateFn;
  t: TranslateFn;
  page: ClosePositionPageEnum;
  pageIndex: number;
  setPageIndex: Dispatch<SetStateAction<number>>;
  setError: Dispatch<SetStateAction<string | undefined>>;
  setWarning: Dispatch<SetStateAction<string | undefined>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
  onHideModal: () => void;
}

export const useClosePositionRFQ = ({
  position,
  getRFQQuotesByBlockId,
  deleteRFQsByInstrumentId,
  rfqData,
  createRFQ,
  acceptQuote,
  addErrorToast,
  addToast,
  apiError,
  t,
  page,
  pageIndex,
  setPageIndex,
  setError,
  setWarning,
  setLoading,
  onHideModal,
}: IUseClosePositionRFQParams) => {
  const [selectedQuote, setQuote] =
    useState<GetRfqsBlockIdQuotes200ResponseAsksInner>();
  const [selectedRfqBlock, setRfqBlock] =
    useState<GetRfqs200ResponseBlocksInner>();

  const invertedSide = useCallback((s: SideResponse) => {
    if (s === SideResponse.Buy) {
      return SideResponse.Sell;
    }
    return SideResponse.Buy;
  }, []);

  useEffect(() => {
    const fetchQuotes = async () => {
      if (selectedRfqBlock && position) {
        const quotes = await getRFQQuotesByBlockId(selectedRfqBlock.block_id);

        if (quotes) {
          const offers = quotes?.asks || [];

          if (offers && offers.length > 0) {
            const offer = offers.find(
              (o) =>
                o.legs &&
                o.legs.length === 1 &&
                String(o.legs[0].instrument_id) === position.instrument_id
            );

            if (offer) setQuote(offer);
          }
        }
      }
    };

    fetchQuotes();
  }, [getRFQQuotesByBlockId, invertedSide, position, selectedRfqBlock]);

  // This hook will check if there are any existing RFQs for this instrument upon entering modal
  // If there is, it will delete before the creation of a new RFQ
  useEffect(() => {
    if (position && !selectedRfqBlock && (rfqData?.blocks || []).length > 0) {
      deleteRFQsByInstrumentId(position.instrument_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position]);

  const hasRequest = useMemo(
    () => Boolean(selectedRfqBlock),
    [selectedRfqBlock]
  );
  const hasQuote = useMemo(() => Boolean(selectedQuote), [selectedQuote]);

  const onDelete = useCallback(async () => {
    if (position) {
      deleteRFQsByInstrumentId(position?.instrument_id);
      setQuote(undefined);
    }
  }, [deleteRFQsByInstrumentId, position]);

  const onPrevPage = useCallback(() => {
    setError("");
    setWarning("");
    switch (page) {
      case ClosePositionPageEnum.REQUEST_RFQ:
        setPageIndex(pageIndex - 1);
        onDelete();
        break;
      default:
        break;
    }
  }, [onDelete, page, pageIndex]);

  const onCreate = useCallback(async () => {
    try {
      // Filter taker's existing RFQs for this instrument_id
      const blocksWithAsset = (rfqData?.blocks || []).some((block) =>
        block.legs?.some((leg) => leg.instrument_id === position?.instrument_id)
      );
      if (position && !blocksWithAsset) {
        // `parseUnits` threw on exponent text and on more places than the token
        // has; `toTokenUnits` floors to the token's decimals instead.
        const amount = toTokenUnits(position.amount, getUsdcDecimals());
        if (!amount) {
          throw new Error(`Invalid position size: ${position.amount}`);
        }
        await createRFQ({
          legs: [
            {
              instrument: position.instrument_id,
              is_buy: invertedSide(position.side) === SideResponse.Buy,
              ratio: 1,
            },
          ],
          full_size: true,
          amount: amount.toString(),
          duration: 1800,
        });
      }
    } catch (e: any) {
      onPrevPage();
      addErrorToast(
        t("create_rfq_failed"),
        apiError(e.message) || t("place_order_again")
      );
    }
  }, [
    addErrorToast,
    apiError,
    createRFQ,
    invertedSide,
    onPrevPage,
    position,
    rfqData?.blocks,
    t,
  ]);

  useEffect(() => {
    if (position && rfqData && (rfqData.blocks || []).length > 0) {
      // There are no roles attached to each rfq from the WSS
      const rfq = (rfqData.blocks || []).find(
        (r) =>
          r.legs &&
          r.legs.length === 1 &&
          String(r.legs[0].instrument_id) === position.instrument_id
      );

      setRfqBlock(rfq);
    } else {
      setRfqBlock(undefined);
    }
  }, [onCreate, position, rfqData]);

  const onAcceptQuote = useCallback(async () => {
    if (position && selectedRfqBlock && selectedQuote) {
      try {
        // Base units as integer text. `String(Number(x) * 10 ** d)` gave float
        // noise and exponent text ("1e+21") that the API cannot read.
        const usdcDecimals = getUsdcDecimals();
        const amount = toTokenUnits(selectedQuote.amount, usdcDecimals);
        const limitPrice = selectedQuote.limit_price
          ? toTokenUnits(selectedQuote.limit_price, usdcDecimals)
          : undefined;
        if (!amount || limitPrice === null) {
          throw new Error("Invalid quote");
        }
        const payload: ITakerQuotePayload = {
          block_id: selectedRfqBlock.block_id,
          amount: amount.toString(),
          is_buy: true,
          limit_price: limitPrice?.toString(),
        };

        setLoading(true);
        const response = await acceptQuote(payload);

        if (response) {
          addToast({
            type: ToastEnum.SIMPLE,
            status: ToastStatusEnum.SUCCESS,
            header: t("offer_accepted"),
          });
          onHideModal();
        }
      } catch (e: any) {
        addErrorToast(t("rfq"), apiError(e.message) || t("try_again"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    acceptQuote,
    addErrorToast,
    addToast,
    apiError,
    onHideModal,
    position,
    selectedQuote,
    selectedRfqBlock,
    t,
  ]);

  const onNextPage = useCallback(() => {
    setError("");
    setWarning("");
    switch (page) {
      case ClosePositionPageEnum.DETAILS:
        setPageIndex(pageIndex + 1);
        onCreate();
        break;
      default:
        break;
    }
  }, [onCreate, page, pageIndex]);

  return {
    selectedQuote,
    selectedRfqBlock,
    hasRequest,
    hasQuote,
    onPrevPage,
    onCreate,
    onAcceptQuote,
    onNextPage,
  };
};
